// Classifieds — intent-first taxonomy per PH2-FE-07.
//
// Step 1: pick INTENT (what you're doing) — Sell, Buy, Offer a service, etc.
// Step 2: pick CATEGORY (what it is about) — filtered to intent-relevant ones.
//
// Card headline reads "Offered: Nanny · R80/hr" instead of "For Sale: Nanny".

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatMastheadDate, timeAgo } from '../lib/format';

type Intent =
    | 'sell' | 'buy' | 'give_away'
    | 'offer_service' | 'want_service'
    | 'housing_offer' | 'housing_want'
    | 'transport_offer' | 'transport_want'
    | 'lost' | 'found' | 'community';

interface Classified {
    id:              string;
    author_user_id:  string;
    author_display?: string;
    intent:          Intent;
    category:        string;
    title:           string;
    body:            string;
    price_text?:     string | null;
    contact?:        string | null;
    location_tag_id?: string | null;
    active:          boolean;
    created_at:      string;
    expires_at:      string;
}

/** Human-friendly labels for the pill row + card headline verb. */
const INTENT_META: Record<Intent, { label: string; verb: string; emoji: string; wantsPrice: boolean }> = {
    sell:            { label: 'Selling',        verb: 'For sale',    emoji: '🏷',  wantsPrice: true  },
    buy:             { label: 'Looking to buy', verb: 'Wanted',      emoji: '🔎',  wantsPrice: true  },
    give_away:       { label: 'Give away',      verb: 'Free',        emoji: '🎁',  wantsPrice: false },
    offer_service:   { label: 'Offering a service', verb: 'Offered', emoji: '🛠',  wantsPrice: true  },
    want_service:    { label: 'Need a service', verb: 'Looking for', emoji: '📣',  wantsPrice: false },
    housing_offer:   { label: 'Housing offer',  verb: 'To let',      emoji: '🏠',  wantsPrice: true  },
    housing_want:    { label: 'Housing wanted', verb: 'Housing wanted', emoji: '🛏', wantsPrice: false },
    transport_offer: { label: 'Transport offer', verb: 'Lift offered', emoji: '🚗', wantsPrice: false },
    transport_want:  { label: 'Transport wanted', verb: 'Lift wanted', emoji: '🚏', wantsPrice: false },
    lost:            { label: 'Lost',           verb: 'Lost',        emoji: '🔍', wantsPrice: false },
    found:           { label: 'Found',          verb: 'Found',       emoji: '👀', wantsPrice: false },
    community:       { label: 'Community',      verb: 'Notice',      emoji: '📢', wantsPrice: false },
};

/** Category suggestions per intent — free-text field so users can override. */
const INTENT_CATEGORIES: Record<Intent, string[]> = {
    sell:            ['For Sale', 'Electronics', 'Furniture', 'Kids', 'Sports', 'Other'],
    buy:             ['Wanted', 'Other'],
    give_away:       ['Free', 'Other'],
    offer_service:   ['Nanny', 'Tutor', 'Cleaner', 'Handyman', 'Piano Lessons', 'Gardener', 'Other'],
    want_service:    ['Nanny', 'Tutor', 'Cleaner', 'Handyman', 'Other'],
    housing_offer:   ['Room To Let', 'Short Let', 'Sublet', 'Other'],
    housing_want:    ['Roommate Wanted', 'House Sit', 'Other'],
    transport_offer: ['Lift Share', 'Vehicle For Sale', 'Other'],
    transport_want:  ['Lift Wanted', 'Other'],
    lost:            ['Pet', 'Wallet/Keys', 'Bike', 'Other'],
    found:           ['Pet', 'Wallet/Keys', 'Other'],
    community:       ['Announcement', 'Warning', 'Meetup', 'Other'],
};

const INTENTS: Intent[] = ['sell', 'buy', 'give_away', 'offer_service', 'want_service',
    'housing_offer', 'housing_want', 'transport_offer', 'transport_want', 'lost', 'found', 'community'];

export function Classifieds() {
    const api = useApi();
    const { authed, login } = useAuth();
    const [items,       setItems]       = useState<Classified[]>([]);
    const [intentFilter, setIntentFilter] = useState<Intent | ''>('');
    const [showForm,    setShowForm]    = useState(false);

    const load = useCallback(async () => {
        if (!authed) return;
        const q = intentFilter ? '?intent=' + intentFilter : '';
        try {
            const r = await api.get<{ classifieds: Classified[] }>('/api/classifieds' + q);
            setItems(r.classifieds ?? []);
        } catch (e) { console.error('classifieds load', e); }
    }, [api, authed, intentFilter]);
    useEffect(() => { load(); }, [load]);

    if (!authed) return (
        <div className="prelogin">
            <p>Log in to browse the classifieds.</p>
            <button type="button" className="plain" onClick={login}>Log in</button>
        </div>
    );

    return (
        <main>
            <div className="paper-wrap">
                <div className="masthead">
                    <div className="rule-top" />
                    <h1>Classifieds</h1>
                    <div className="issue">{formatMastheadDate()}</div>
                    <div className="rule-bottom" />
                </div>

                <div className="classifieds-toolbar">
                    <div className="intent-pills">
                        <button type="button"
                                className={'intent-pill' + (intentFilter === '' ? ' is-active' : '')}
                                onClick={() => setIntentFilter('')}>All</button>
                        {INTENTS.map(i => (
                            <button type="button"
                                    key={i}
                                    className={'intent-pill' + (intentFilter === i ? ' is-active' : '')}
                                    onClick={() => setIntentFilter(i)}>
                                {INTENT_META[i].emoji} {INTENT_META[i].label}
                            </button>
                        ))}
                    </div>
                    <button type="button" className="plain" onClick={() => setShowForm(v => !v)}>
                        {showForm ? 'Cancel' : '+ Post a listing'}
                    </button>
                </div>

                {showForm && <NewForm onCreated={async () => { setShowForm(false); await load(); }} />}

                {items.length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#999', fontStyle: 'italic', fontFamily: "'Newsreader',serif", padding: '40px 0' }}>
                        {intentFilter
                            ? `Nothing under ${INTENT_META[intentFilter].label} right now.`
                            : 'Nothing here yet. Hit "+ Post a listing" to add the first one.'}
                    </p>
                ) : (
                    <div className="classifieds-grid">
                        {items.map(c => {
                            const meta = INTENT_META[c.intent] ?? INTENT_META.sell;
                            return (
                                <div className="ad" key={c.id}>
                                    <div className="ad-head">
                                        <strong>{meta.emoji} {meta.verb}</strong>
                                        {' · '}{c.category}
                                        {c.price_text && <span className="ad-price"> · {c.price_text}</span>}
                                    </div>
                                    <div className="ad-title">{c.title}</div>
                                    <div className="ad-body">{c.body}</div>
                                    <div className="ad-byline">— {c.author_display || 'Anon'}, {timeAgo(c.created_at)}</div>
                                    {c.contact && <div className="ad-contact">Contact: {c.contact}</div>}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </main>
    );
}

function NewForm({ onCreated }: { onCreated: () => void }) {
    const api = useApi();
    const [intent,   setIntent]   = useState<Intent>('sell');
    const [category, setCategory] = useState('For Sale');
    const [title,    setTitle]    = useState('');
    const [body,     setBody]     = useState('');
    const [price,    setPrice]    = useState('');
    const [contact,  setContact]  = useState('');
    const [status,   setStatus]   = useState<{ tone: 'ok' | 'err' | 'busy'; msg: string } | null>(null);

    // Keep the category in sync with the intent's suggested first value.
    useEffect(() => { setCategory(INTENT_CATEGORIES[intent][0]); }, [intent]);

    async function onSubmit(e: FormEvent) {
        e.preventDefault();
        setStatus({ tone: 'busy', msg: 'Posting…' });
        try {
            await api.post('/api/classifieds', {
                intent, category, title, body,
                price_text: price   || null,
                contact:    contact || null,
            });
            setStatus({ tone: 'ok', msg: '✓ Listed' });
            setTimeout(onCreated, 800);
        } catch (err) { setStatus({ tone: 'err', msg: (err as Error).message }); }
    }

    const meta = INTENT_META[intent];
    return (
        <form className="classified-form" onSubmit={onSubmit}>
            <label>
                <span>What are you doing?</span>
                <div className="intent-pills" style={{ marginTop: 4 }}>
                    {INTENTS.map(i => (
                        <button type="button" key={i}
                                className={'intent-pill' + (intent === i ? ' is-active' : '')}
                                onClick={() => setIntent(i)}>
                            {INTENT_META[i].emoji} {INTENT_META[i].label}
                        </button>
                    ))}
                </div>
            </label>
            <label>
                <span>Category</span>
                <input type="text" list="cat-options" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Pick one or type your own" />
                <datalist id="cat-options">
                    {INTENT_CATEGORIES[intent].map(c => <option key={c} value={c} />)}
                </datalist>
            </label>
            <label>
                <span>Title</span>
                <input type="text" required maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} placeholder='e.g. "Weber kettle braai, gently used"' />
            </label>
            <label>
                <span>Details</span>
                <textarea required maxLength={4000} rows={3} value={body} onChange={(e) => setBody(e.target.value)} />
            </label>
            <label>
                <span>{meta.wantsPrice ? 'Price / rate (optional)' : 'Deal (optional)'}</span>
                <input type="text" maxLength={80} value={price} onChange={(e) => setPrice(e.target.value)}
                       placeholder={meta.wantsPrice
                           ? 'e.g. "R450 ono", "R80/hr", "Swap for garden tools"'
                           : 'e.g. "Reward if returned"'} />
            </label>
            <label>
                <span>Contact (optional)</span>
                <input type="text" maxLength={200} value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Phone or email — visible to logged-in users" />
            </label>
            <div className="actions">
                <button type="submit" className="primary">Post as {meta.label.toLowerCase()}</button>
                {status && <span className={status.tone === 'err' ? 'err' : status.tone === 'ok' ? 'ok' : ''}>{status.msg}</span>}
            </div>
        </form>
    );
}
